/**
 * Chart Kit – schlanke SVG-Charts für das Owner-/Admin-Dashboard.
 *
 * Bewusst ohne Chart-Library: reines SVG, an das Design-System der App gekoppelt,
 * responsiv über ResizeObserver (echte Pixel-Geometrie statt viewBox-Skalierung,
 * damit Schriftgrößen nicht verzerren).
 *
 * Farbregeln (validiert gegen weißen Kartenhintergrund):
 * - Kategorial (Studios): feste Slot-Reihenfolge, nie durchrotiert.
 * - Einzelne Serie: eine Farbe (Sequential-Default Blau).
 * - Divergierend (Zugänge/Abgänge): Blau <-> Rot mit neutraler Nulllinie.
 * Jedes Chart hat zusätzlich eine Tabellenansicht, damit kein Wert nur über
 * Hover oder nur über Farbe erreichbar ist.
 */

import React, { useState, useRef, useEffect, useCallback, useMemo, useId } from 'react';
import './Charts.css';

// ========================
// Farb-Tokens
// ========================

/** Kategoriale Slots in fixer Reihenfolge (Identität folgt der Entität, nicht dem Rang). */
export const SERIES_COLORS = [
  '#2a78d6', // blau
  '#eb6834', // orange
  '#1baf7a', // aqua
  '#eda100', // gelb
  '#e87ba4', // magenta
  '#008300', // grün
  '#4a3aa7', // violett
  '#e34948', // rot
];

/** Einzelserien-Farbe (Sequential-Default). */
export const ACCENT = '#2a78d6';

/** Divergierendes Paar: warm/kalt, damit die Pole als Gegensatz lesbar sind. */
export const DIVERGING = { positive: '#2a78d6', negative: '#e34948' };

/** Mitglieder-Lebenszyklus – feste Zuordnung, damit ein Status immer gleich aussieht. */
export const LIFECYCLE_COLORS = {
  active: '#1baf7a',
  cancelling: '#eda100',
  paused: '#2a78d6',
  cancelled: '#e34948',
  prospect: '#4a3aa7',
  inactive: '#94a3b8', // "kein Zustand" bekommt bewusst das De-Emphasis-Grau
};

const INK = {
  primary: '#1e293b',
  secondary: '#64748b',
  muted: '#94a3b8',
  grid: '#e8ecf1',
  axis: '#cbd5e1',
  surface: '#ffffff',
};

export const seriesColor = (i) => SERIES_COLORS[i % SERIES_COLORS.length];

// ========================
// Helfer
// ========================

/** Misst die Containerbreite, damit das SVG mit echten Pixeln rechnen kann. */
function useMeasure() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, width];
}

/** Achsen-Ticks auf runde Zahlen (0 / 20 / 40 …). */
function niceTicks(max, count = 4) {
  if (!isFinite(max) || max <= 0) return { ticks: [0, 1], top: 1 };
  const raw = max / count;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag;
  const top = Math.ceil(max / step) * step;
  const ticks = [];
  for (let v = 0; v <= top + step / 1000; v += step) ticks.push(Number(v.toFixed(6)));
  return { ticks, top };
}

/** Balken mit 4px-Radius am Datenende, eckig an der Grundlinie. */
function barPath(x, y, w, h, r = 4, down = false) {
  if (h <= 0) return '';
  const rr = Math.max(0, Math.min(r, w / 2, h));
  if (down) {
    // wächst nach unten -> Rundung unten
    return `M ${x} ${y} L ${x + w} ${y} L ${x + w} ${y + h - rr} Q ${x + w} ${y + h} ${x + w - rr} ${y + h} L ${x + rr} ${y + h} Q ${x} ${y + h} ${x} ${y + h - rr} Z`;
  }
  return `M ${x} ${y + h} L ${x} ${y + rr} Q ${x} ${y} ${x + rr} ${y} L ${x + w - rr} ${y} Q ${x + w} ${y} ${x + w} ${y + rr} L ${x + w} ${y + h} Z`;
}

const defaultFormat = (v) => new Intl.NumberFormat('de-AT').format(Math.round(v * 100) / 100);

/**
 * Welche X-Positionen beschriftet werden: gleichmäßig ausgedünnt, das letzte
 * Label immer dabei – und das vorletzte fliegt raus, wenn es zu nah steht.
 */
function axisLabelIndices(count, plotWidth, xAt, minPx = 52) {
  if (count <= 1 || plotWidth <= 0) return [0];
  const fits = Math.max(1, Math.floor(plotWidth / minPx));
  const step = Math.max(1, Math.ceil(count / fits));
  const idx = [];
  for (let i = 0; i < count; i += step) idx.push(i);
  const last = idx[idx.length - 1];
  if (last !== count - 1) {
    if (xAt(count - 1) - xAt(last) < minPx) idx.pop();
    idx.push(count - 1);
  }
  return idx;
}

/** Randlabels nach innen ausrichten, damit sie nicht aus dem SVG laufen. */
const anchorFor = (i, count) => (i === 0 ? 'start' : i === count - 1 ? 'end' : 'middle');

// ========================
// Tooltip
// ========================

function Tooltip({ x, y, width, title, rows }) {
  if (x == null) return null;
  // Tooltip an der Kante spiegeln, damit er nie aus der Karte läuft
  const flip = x > width * 0.6;
  return (
    <div
      className={`viz-tooltip ${flip ? 'flip' : ''}`}
      style={{ left: x, top: y }}
      role="status"
      aria-live="polite"
    >
      {title && <div className="viz-tooltip-title">{title}</div>}
      {rows.map((r, i) => (
        <div key={i} className="viz-tooltip-row">
          {r.color && <span className="viz-tooltip-key" style={{ background: r.color }} />}
          <span className="viz-tooltip-value">{r.value}</span>
          <span className="viz-tooltip-name">{r.name}</span>
        </div>
      ))}
    </div>
  );
}

// ========================
// Legende
// ========================

function Legend({ items, shape = 'line' }) {
  if (!items || items.length < 2) return null;
  return (
    <div className="viz-legend">
      {items.map((it) => (
        <span key={it.id ?? it.name} className="viz-legend-item">
          <span
            className={`viz-legend-key ${shape === 'rect' ? 'rect' : 'line'}`}
            style={{ background: it.color }}
          />
          <span className="viz-legend-name">{it.name}</span>
          {it.value != null && <span className="viz-legend-value">{it.value}</span>}
        </span>
      ))}
    </div>
  );
}

// ========================
// Karte mit Tabellen-Zwilling
// ========================

/**
 * Rahmen für jedes Chart: Titel, optionale Bedienelemente und ein Umschalter
 * auf die Tabellenansicht (Pflicht-Zwilling – jeder Wert bleibt ohne Hover lesbar).
 */
export function ChartCard({ title, subtitle, actions, table, footnote, children, className = '' }) {
  const [showTable, setShowTable] = useState(false);
  const hasTable = table && table.columns && table.rows && table.rows.length > 0;

  return (
    <section className={`viz-card ${className}`}>
      <header className="viz-card-head">
        <div className="viz-card-titles">
          <h3 className="viz-card-title">{title}</h3>
          {subtitle && <p className="viz-card-subtitle">{subtitle}</p>}
        </div>
        <div className="viz-card-actions">
          {actions}
          {hasTable && (
            <button
              type="button"
              className="viz-toggle-table"
              onClick={() => setShowTable((s) => !s)}
              aria-pressed={showTable}
            >
              {showTable ? 'Diagramm' : 'Tabelle'}
            </button>
          )}
        </div>
      </header>

      {hasTable && showTable ? (
        <div className="viz-table-wrap">
          <table className="viz-table">
            <thead>
              <tr>{table.columns.map((c, i) => <th key={i} className={i === 0 ? 'left' : ''}>{c}</th>)}</tr>
            </thead>
            <tbody>
              {table.rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => <td key={ci} className={ci === 0 ? 'left' : ''}>{cell}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        children
      )}

      {footnote && <p className="viz-card-footnote">{footnote}</p>}
    </section>
  );
}

// ========================
// Segmented Control (Filterzeile über den Charts)
// ========================

export function SegmentedControl({ options, value, onChange, ariaLabel }) {
  return (
    <div className="viz-segmented" role="group" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={value === o.value ? 'active' : ''}
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ========================
// Linien-Chart (Verlauf über Zeit)
// ========================

/**
 * @param categories string[]           X-Achsenbeschriftungen
 * @param series     [{id,name,color,values:number[]}]
 * @param area       boolean            Flächenwäsche (nur bei einer Serie sinnvoll)
 */
export function LineChart({
  categories = [],
  tips,
  series = [],
  height = 260,
  formatValue = defaultFormat,
  area,
  ariaLabel = 'Liniendiagramm',
  emptyText = 'Keine Daten',
}) {
  const [ref, width] = useMeasure();
  const [active, setActive] = useState(null);
  const clipId = useId().replace(/:/g, '');

  const n = categories.length;
  const showArea = area !== undefined ? area : series.length === 1;

  const pad = { top: 22, right: 16, bottom: 30, left: 46 };
  const plotW = Math.max(0, width - pad.left - pad.right);
  const plotH = Math.max(0, height - pad.top - pad.bottom);

  const maxVal = useMemo(
    () => series.reduce((m, s) => Math.max(m, ...s.values.map((v) => v || 0)), 0),
    [series]
  );
  const { ticks, top } = useMemo(() => niceTicks(maxVal), [maxVal]);

  const xAt = useCallback((i) => pad.left + (n > 1 ? (i / (n - 1)) * plotW : plotW / 2), [n, plotW, pad.left]);
  const yAt = useCallback((v) => pad.top + plotH - ((v || 0) / top) * plotH, [plotH, top, pad.top]);

  const handleMove = (e) => {
    if (n === 0 || plotW <= 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const rel = e.clientX - rect.left - pad.left;
    const idx = n > 1 ? Math.round((rel / plotW) * (n - 1)) : 0;
    setActive(Math.max(0, Math.min(n - 1, idx)));
  };

  const handleKey = (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    setActive((a) => {
      const cur = a == null ? 0 : a;
      return Math.max(0, Math.min(n - 1, cur + (e.key === 'ArrowRight' ? 1 : -1)));
    });
  };

  if (n === 0 || series.length === 0) return <div className="viz-empty">{emptyText}</div>;

  const labelIdx = axisLabelIndices(n, plotW, xAt);
  const markerEvery = n <= 24; // bei vielen Punkten nur Endpunkt + Hover markieren

  return (
    <div className="viz-chart" ref={ref}>
      {width > 0 && (
        <div className="viz-plot" style={{ height }}>
          <svg width={width} height={height} role="img" aria-label={ariaLabel}>
            <defs>
              <clipPath id={`clip-${clipId}`}>
                <rect x={pad.left} y={pad.top - 4} width={plotW} height={plotH + 4} />
              </clipPath>
            </defs>

            {/* Raster + Y-Achse */}
            {ticks.map((t) => (
              <g key={t}>
                <line x1={pad.left} y1={yAt(t)} x2={pad.left + plotW} y2={yAt(t)} stroke={INK.grid} strokeWidth="1" />
                <text x={pad.left - 8} y={yAt(t) + 4} textAnchor="end" className="viz-axis-text">{formatValue(t)}</text>
              </g>
            ))}

            {/* X-Achse */}
            {labelIdx.map((i) => (
              <text
                key={i}
                x={i === 0 ? pad.left : i === n - 1 ? pad.left + plotW : xAt(i)}
                y={height - pad.bottom + 18}
                textAnchor={anchorFor(i, n)}
                className="viz-axis-text"
              >
                {categories[i]}
              </text>
            ))}

            {/* Crosshair */}
            {active != null && (
              <line x1={xAt(active)} y1={pad.top - 4} x2={xAt(active)} y2={pad.top + plotH} stroke={INK.axis} strokeWidth="1" />
            )}

            <g clipPath={`url(#clip-${clipId})`}>
              {series.map((s, si) => {
                const color = s.color || seriesColor(si);
                const pts = s.values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(v)}`).join(' ');
                const areaD = `${pts} L ${xAt(n - 1)} ${pad.top + plotH} L ${xAt(0)} ${pad.top + plotH} Z`;
                return (
                  <g key={s.id ?? si}>
                    {showArea && <path d={areaD} fill={color} opacity="0.1" />}
                    <path d={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                    {markerEvery && s.values.map((v, i) => (
                      <circle key={i} cx={xAt(i)} cy={yAt(v)} r="4" fill={color} stroke={INK.surface} strokeWidth="2" />
                    ))}
                    {!markerEvery && (
                      <circle cx={xAt(n - 1)} cy={yAt(s.values[n - 1])} r="4" fill={color} stroke={INK.surface} strokeWidth="2" />
                    )}
                    {active != null && (
                      <circle cx={xAt(active)} cy={yAt(s.values[active])} r="5" fill={color} stroke={INK.surface} strokeWidth="2" />
                    )}
                  </g>
                );
              })}
            </g>

            {/* Endwert direkt beschriften – nur bei einer Serie, sonst trägt die Legende die Werte */}
            {series.length === 1 && (
              <text
                x={xAt(n - 1)}
                y={yAt(series[0].values[n - 1]) - 12}
                textAnchor="end"
                className="viz-point-label"
              >
                {formatValue(series[0].values[n - 1])}
              </text>
            )}

            {/* Interaktionsfläche */}
            <rect
              x={0} y={0} width={width} height={height}
              fill="transparent"
              tabIndex={0}
              onPointerMove={handleMove}
              onPointerLeave={() => setActive(null)}
              onFocus={() => setActive((a) => (a == null ? n - 1 : a))}
              onBlur={() => setActive(null)}
              onKeyDown={handleKey}
              style={{ outline: 'none', cursor: 'crosshair' }}
            />
          </svg>

          {active != null && (
            <Tooltip
              x={xAt(active)}
              y={pad.top}
              width={width}
              title={(tips && tips[active]) || categories[active]}
              rows={series.map((s, si) => ({
                color: s.color || seriesColor(si),
                name: s.name,
                value: formatValue(s.values[active] || 0),
              }))}
            />
          )}
        </div>
      )}

      <Legend
        shape="line"
        items={series.map((s, si) => ({
          id: s.id ?? si,
          name: s.name,
          color: s.color || seriesColor(si),
          value: formatValue(s.values[n - 1] || 0),
        }))}
      />
    </div>
  );
}

// ========================
// Säulen-Chart (einzeln oder gestapelt)
// ========================

export function ColumnChart({
  categories = [],
  tips,
  series = [],
  stacked = false,
  height = 240,
  formatValue = defaultFormat,
  ariaLabel = 'Säulendiagramm',
  emptyText = 'Keine Daten',
  labelMode = 'extremes', // 'extremes' | 'none'
}) {
  const [ref, width] = useMeasure();
  const [active, setActive] = useState(null);

  const n = categories.length;
  const pad = { top: 24, right: 16, bottom: 30, left: 46 };
  const plotW = Math.max(0, width - pad.left - pad.right);
  const plotH = Math.max(0, height - pad.top - pad.bottom);

  const totals = useMemo(
    () => categories.map((_, i) =>
      stacked
        ? series.reduce((sum, s) => sum + (s.values[i] || 0), 0)
        : Math.max(...series.map((s) => s.values[i] || 0), 0)
    ),
    [categories, series, stacked]
  );
  const { ticks, top } = useMemo(() => niceTicks(Math.max(...totals, 0)), [totals]);

  if (n === 0 || series.length === 0) return <div className="viz-empty">{emptyText}</div>;

  const band = plotW / n;
  const groupW = Math.min(24 * (stacked ? 1 : series.length), band * 0.62);
  const barW = stacked ? groupW : groupW / series.length;
  const GAP = 2; // Flächenabstand statt Kontur

  const bandCenter = (i) => pad.left + band * i + band / 2;
  const labelIdx = axisLabelIndices(n, plotW, bandCenter);
  const maxIdx = totals.indexOf(Math.max(...totals));
  const yAt = (v) => pad.top + plotH - (v / top) * plotH;

  return (
    <div className="viz-chart" ref={ref}>
      {width > 0 && (
        <div className="viz-plot" style={{ height }}>
          <svg width={width} height={height} role="img" aria-label={ariaLabel}>
            {ticks.map((t) => (
              <g key={t}>
                <line x1={pad.left} y1={yAt(t)} x2={pad.left + plotW} y2={yAt(t)} stroke={INK.grid} strokeWidth="1" />
                <text x={pad.left - 8} y={yAt(t) + 4} textAnchor="end" className="viz-axis-text">{formatValue(t)}</text>
              </g>
            ))}

            {labelIdx.map((i) => (
              <text key={i} x={bandCenter(i)} y={height - pad.bottom + 18} textAnchor="middle" className="viz-axis-text">
                {categories[i]}
              </text>
            ))}

            {categories.map((c, i) => {
              const cx = pad.left + band * i + band / 2;
              const isActive = active === i;
              let stackTop = pad.top + plotH;

              return (
                <g key={i} className={isActive ? 'viz-col active' : 'viz-col'}>
                  {series.map((s, si) => {
                    const v = s.values[i] || 0;
                    const color = s.color || seriesColor(si);
                    if (v <= 0) return null;
                    const h = (v / top) * plotH;

                    if (stacked) {
                      const segH = Math.max(0, h - GAP);
                      const y = stackTop - h;
                      stackTop -= h;
                      const isTopSeg = series.slice(si + 1).every((o) => !(o.values[i] > 0));
                      return (
                        <path
                          key={s.id ?? si}
                          d={barPath(cx - barW / 2, y + (isTopSeg ? 0 : GAP), barW, isTopSeg ? h : segH, isTopSeg ? 4 : 0)}
                          fill={color}
                          opacity={isActive ? 1 : 0.92}
                        />
                      );
                    }

                    const x = cx - groupW / 2 + si * barW;
                    return (
                      <path
                        key={s.id ?? si}
                        d={barPath(x + GAP / 2, pad.top + plotH - h, Math.max(1, barW - GAP), h, 4)}
                        fill={color}
                        opacity={isActive ? 1 : 0.92}
                      />
                    );
                  })}

                  {/* Nur Extremwert und letzter Wert direkt beschriften */}
                  {labelMode === 'extremes' && (i === maxIdx || i === n - 1) && totals[i] > 0 && (
                    <text x={cx} y={yAt(totals[i]) - 8} textAnchor="middle" className="viz-point-label">
                      {formatValue(totals[i])}
                    </text>
                  )}

                  {/* Trefferfläche über die ganze Bandbreite */}
                  <rect
                    x={pad.left + band * i} y={pad.top} width={band} height={plotH}
                    fill="transparent"
                    onPointerEnter={() => setActive(i)}
                    onPointerLeave={() => setActive(null)}
                  />
                </g>
              );
            })}
          </svg>

          {active != null && (
            <Tooltip
              x={pad.left + band * active + band / 2}
              y={pad.top}
              width={width}
              title={(tips && tips[active]) || categories[active]}
              rows={[
                ...series.map((s, si) => ({
                  color: s.color || seriesColor(si),
                  name: s.name,
                  value: formatValue(s.values[active] || 0),
                })),
                ...(stacked && series.length > 1
                  ? [{ color: null, name: 'Gesamt', value: formatValue(totals[active]) }]
                  : []),
              ]}
            />
          )}
        </div>
      )}

      <Legend
        shape="rect"
        items={series.map((s, si) => ({ id: s.id ?? si, name: s.name, color: s.color || seriesColor(si) }))}
      />
    </div>
  );
}

// ========================
// Divergierendes Säulen-Chart (Zugänge über, Abgänge unter der Nulllinie)
// ========================

export function DivergingColumnChart({
  categories = [],
  tips,
  positive = { name: 'Zugänge', values: [] },
  negative = { name: 'Abgänge', values: [] },
  height = 220,
  formatValue = defaultFormat,
  ariaLabel = 'Zu- und Abgänge',
  emptyText = 'Keine Daten',
}) {
  const [ref, width] = useMeasure();
  const [active, setActive] = useState(null);

  const n = categories.length;
  const pad = { top: 18, right: 16, bottom: 30, left: 46 };
  const plotW = Math.max(0, width - pad.left - pad.right);
  const plotH = Math.max(0, height - pad.top - pad.bottom);

  const maxVal = Math.max(...positive.values.map((v) => v || 0), ...negative.values.map((v) => v || 0), 0);
  const { ticks, top } = niceTicks(maxVal, 2);

  if (n === 0) return <div className="viz-empty">{emptyText}</div>;

  const zeroY = pad.top + plotH / 2;
  const halfH = plotH / 2;
  const band = plotW / n;
  const barW = Math.min(20, band * 0.5);
  const posColor = DIVERGING.positive;
  const negColor = DIVERGING.negative;
  const bandCenter = (i) => pad.left + band * i + band / 2;
  const labelIdx = axisLabelIndices(n, plotW, bandCenter);

  return (
    <div className="viz-chart" ref={ref}>
      {width > 0 && (
        <div className="viz-plot" style={{ height }}>
          <svg width={width} height={height} role="img" aria-label={ariaLabel}>
            {ticks.filter((t) => t > 0).map((t) => (
              <g key={t}>
                <line x1={pad.left} y1={zeroY - (t / top) * halfH} x2={pad.left + plotW} y2={zeroY - (t / top) * halfH} stroke={INK.grid} strokeWidth="1" />
                <line x1={pad.left} y1={zeroY + (t / top) * halfH} x2={pad.left + plotW} y2={zeroY + (t / top) * halfH} stroke={INK.grid} strokeWidth="1" />
                <text x={pad.left - 8} y={zeroY - (t / top) * halfH + 4} textAnchor="end" className="viz-axis-text">{formatValue(t)}</text>
                <text x={pad.left - 8} y={zeroY + (t / top) * halfH + 4} textAnchor="end" className="viz-axis-text">{formatValue(t)}</text>
              </g>
            ))}
            <line x1={pad.left} y1={zeroY} x2={pad.left + plotW} y2={zeroY} stroke={INK.axis} strokeWidth="1" />
            <text x={pad.left - 8} y={zeroY + 4} textAnchor="end" className="viz-axis-text">0</text>

            {categories.map((c, i) => {
              const cx = pad.left + band * i + band / 2;
              const p = positive.values[i] || 0;
              const nv = negative.values[i] || 0;
              const ph = (p / top) * halfH;
              const nh = (nv / top) * halfH;
              return (
                <g key={i}>
                  {p > 0 && <path d={barPath(cx - barW / 2, zeroY - ph - 1, barW, ph, 4)} fill={posColor} opacity={active === i ? 1 : 0.92} />}
                  {nv > 0 && <path d={barPath(cx - barW / 2, zeroY + 1, barW, nh, 4, true)} fill={negColor} opacity={active === i ? 1 : 0.92} />}
                  {labelIdx.includes(i) && (
                    <text x={cx} y={height - pad.bottom + 18} textAnchor="middle" className="viz-axis-text">{c}</text>
                  )}
                  <rect
                    x={pad.left + band * i} y={pad.top} width={band} height={plotH}
                    fill="transparent"
                    onPointerEnter={() => setActive(i)}
                    onPointerLeave={() => setActive(null)}
                  />
                </g>
              );
            })}
          </svg>

          {active != null && (
            <Tooltip
              x={pad.left + band * active + band / 2}
              y={pad.top}
              width={width}
              title={(tips && tips[active]) || categories[active]}
              rows={[
                { color: posColor, name: positive.name, value: `+${formatValue(positive.values[active] || 0)}` },
                { color: negColor, name: negative.name, value: `−${formatValue(negative.values[active] || 0)}` },
                { color: null, name: 'Netto', value: formatValue((positive.values[active] || 0) - (negative.values[active] || 0)) },
              ]}
            />
          )}
        </div>
      )}

      <Legend shape="rect" items={[
        { id: 'pos', name: positive.name, color: posColor },
        { id: 'neg', name: negative.name, color: negColor },
      ]} />
    </div>
  );
}

// ========================
// Rangliste (horizontale Balken)
// ========================

export function BarList({ items = [], formatValue = defaultFormat, color = ACCENT, unit = '', labelWidth = 72, valueWidth = 64, emptyText = 'Keine Daten' }) {
  if (items.length === 0) return <div className="viz-empty">{emptyText}</div>;
  const max = Math.max(...items.map((i) => i.value || 0), 1);
  const cols = { gridTemplateColumns: `minmax(0, ${labelWidth}px) 1fr ${valueWidth}px` };

  return (
    <ul className="viz-barlist">
      {items.map((it, i) => (
        <li key={it.label ?? i} className="viz-barlist-row" style={cols}>
          <span className="viz-barlist-label">{it.label}</span>
          <span className="viz-barlist-track">
            <span
              className="viz-barlist-bar"
              style={{ width: `${Math.max((it.value / max) * 100, 1)}%`, background: color }}
            />
          </span>
          <span className="viz-barlist-value">{formatValue(it.value)}{unit}</span>
        </li>
      ))}
    </ul>
  );
}

// ========================
// Anteilsbalken (Teil vom Ganzen)
// ========================

export function ShareBar({ segments = [], formatValue = defaultFormat, compact = false }) {
  const total = segments.reduce((s, x) => s + (x.value || 0), 0);
  if (total <= 0) return <div className="viz-empty">Keine Daten</div>;

  return (
    <div className={`viz-sharebar-wrap ${compact ? 'compact' : ''}`}>
      <div className="viz-sharebar">
        {segments.filter((s) => s.value > 0).map((s) => {
          const pct = (s.value / total) * 100;
          // Zahl nur einsetzen, wenn sie mit Luft hineinpasst – sonst trägt sie die Legende
          const fits = pct >= 10;
          return (
            <div
              key={s.label}
              className="viz-sharebar-seg"
              style={{ width: `${pct}%`, background: s.color }}
              title={`${s.label}: ${formatValue(s.value)} (${Math.round(pct)}%)`}
            >
              {fits && <span className="viz-sharebar-num">{formatValue(s.value)}</span>}
            </div>
          );
        })}
      </div>
      {!compact && <div className="viz-legend">
        {segments.filter((s) => s.value > 0).map((s) => (
          <span key={s.label} className="viz-legend-item">
            <span className="viz-legend-key rect" style={{ background: s.color }} />
            <span className="viz-legend-name">{s.label}</span>
            <span className="viz-legend-value">{formatValue(s.value || 0)}</span>
          </span>
        ))}
      </div>}
    </div>
  );
}

// ========================
// Sparkline + Kennzahlkachel
// ========================

export function Sparkline({ values = [], color = ACCENT, width = 96, height = 28 }) {
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const x = (i) => (i / (values.length - 1)) * (width - 4) + 2;
  const y = (v) => height - 3 - ((v - min) / span) * (height - 6);
  const d = values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(v)}`).join(' ');

  return (
    <svg className="viz-sparkline" width={width} height={height} aria-hidden="true">
      <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" opacity="0.45" />
      <circle cx={x(values.length - 1)} cy={y(values[values.length - 1])} r="3.5" fill={color} stroke={INK.surface} strokeWidth="2" />
    </svg>
  );
}

/**
 * @param tone 'neutral' | 'good' | 'bad'  – färbt nur den Wert, nicht die Kachel
 * @param delta {value, label, goodWhen: 'up'|'down'}
 */
export function StatTile({ label, value, sub, delta, trend, trendColor, tone = 'neutral', hero = false, children }) {
  let deltaClass = 'flat';
  if (delta && delta.value !== 0 && delta.value != null) {
    const up = delta.value > 0;
    const good = delta.goodWhen === 'down' ? !up : up;
    deltaClass = good ? 'good' : 'bad';
  }

  return (
    <div className={`viz-stat ${hero ? 'hero' : ''} tone-${tone}`}>
      <div className="viz-stat-label">{label}</div>
      <div className="viz-stat-value-row">
        <span className="viz-stat-value">{value}</span>
        {trend && trend.length > 1 && <Sparkline values={trend} color={trendColor || ACCENT} />}
      </div>
      {(delta || sub) && (
        <div className="viz-stat-foot">
          {delta && (
            <span className={`viz-stat-delta ${deltaClass}`}>
              <span aria-hidden="true">{delta.value > 0 ? '▲' : delta.value < 0 ? '▼' : '■'}</span>
              {delta.value > 0 ? '+' : ''}{delta.text ?? delta.value}
              {delta.label && <span className="viz-stat-delta-label">{delta.label}</span>}
            </span>
          )}
          {sub && <span className="viz-stat-sub">{sub}</span>}
        </div>
      )}
      {children && <div className="viz-stat-extra">{children}</div>}
    </div>
  );
}

export function StatGrid({ children }) {
  return <div className="viz-stat-grid">{children}</div>;
}
